import { staffFieldRoot } from "./fieldStyles";

interface InputTextAreaProps {
  title?: string;
  name?: string;
  id?: string;
  required?: boolean;
  onChange?: (value: string) => void;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  tooltip?: string;
  className?: string;
  textareaClassName?: string;
  readOnly?: boolean;
  showAsLabel?: boolean;
  maxChars?: number;
  showCharacterCount?: boolean;
  rows?: number;
  minHeightClassName?: string;
  onBlur?: () => void;
}

const baseInputClasses =
  "w-full rounded-md text-left text-sm transition-colors flex-1";

const InputTextArea = ({
  title,
  name,
  id,
  required = false,
  onChange,
  value,
  placeholder,
  disabled = false,
  error = false,
  errorMessage,
  tooltip,
  className = "",
  textareaClassName = "",
  readOnly = false,
  showAsLabel = false,
  maxChars,
  showCharacterCount = false,
  rows = 1,
  minHeightClassName = "min-h-[20px]",
  onBlur,
}: InputTextAreaProps) => {
  const handleChange = (next: string) => {
    let v = next;
    if (maxChars !== undefined && v.length > maxChars) {
      v = v.slice(0, maxChars);
    }
    onChange?.(v);
  };

  const renderAsLabel = readOnly || (showAsLabel && disabled);

  return (
    <div className={`${staffFieldRoot} ${className}`}>
      {title && (
        <label
          htmlFor={renderAsLabel ? undefined : id ?? name}
          title={tooltip}
          className="font-semibold text-gray-font"
        >
          {title}
          {required && <span className="text-primary"> *</span>}
        </label>
      )}

      {renderAsLabel ? (
        <p className="whitespace-pre-wrap py-[11px] text-sm text-gray-font">
          {value?.trim() ? value : "-"}
        </p>
      ) : (
        <>
          <div
            className={`${baseInputClasses} px-3 py-[10px] ${error
              ? "bg-gray-25 text-primary outline outline-2 outline-primary"
              : disabled
                ? "cursor-not-allowed bg-gray-100 text-gray-400 outline outline-2 outline-gray-200"
                : "bg-gray-25 text-input-gray"
              }`}
          >
            <textarea
              id={id ?? name}
              name={name}
              rows={rows}
              placeholder={placeholder}
              disabled={disabled}
              value={value}
              onChange={(event) => handleChange(event.target.value)}
              required={required}
              className={`block w-full resize-y p-0 text-sm leading-5 text-gray-font placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:!outline-none disabled:bg-transparent disabled:text-gray-400 ${minHeightClassName} ${textareaClassName}`}
              onBlur={() => onBlur?.()}
            />
          </div>

          {showCharacterCount && maxChars !== undefined && (
            <p className="ml-auto text-sm text-gray-font">
              {value.length}/{maxChars}
            </p>
          )}
        </>
      )}

      {error && errorMessage && !renderAsLabel && (
        <span className="text-primary">{errorMessage}</span>
      )}
    </div>
  );
};

export default InputTextArea;
